<!--@format-->
<template>
    <div class="magic-branding-button-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>{{ title }}</h3>
                <div class="widget-header-actions">
                    <div class="svg-container widget-close-button" @click="closeWidget">
                        <widget-close-svg />
                    </div>
                </div>
            </div>
            <div class="content-wrapper">
                <div class="settings-section">
                    <h4 class="section-title">Button Settings</h4>
                    <div class="settings-container">
                        <div class="section-item">
                            <span class="item-title">Button Text</span>
                            <branding-color-setting
                                :color="currentConfiguration.textColor.value"
                                :main-brand-colors="mainBrandColors"
                                :default-colors="defaultColors"
                                @color-change="handleColorChange($event, 'textColor')"
                            />
                        </div>

                        <div class="section-item">
                            <span class="item-title">Button Color</span>
                            <branding-color-setting
                                :color="currentConfiguration.buttonColor.value"
                                :main-brand-colors="mainBrandColors"
                                :default-colors="defaultColors"
                                @color-change="handleColorChange($event, 'buttonColor')"
                            />
                        </div>

                        <div class="section-item">
                            <span class="item-title">Font</span>
                            <app-font-select
                                :custom-font-sizes="fontSizes"
                                :font-size="currentConfiguration.fontSize"
                                :font-family="currentConfiguration.fontFamily"
                                @select:font-size="updateFontSize($event)"
                                @select:font-family="updateFontFamily($event)"
                            />
                        </div>

                        <div class="section-item no-margin">
                            <span class="item-title">Button Style</span>
                            <button-fill-select v-model="currentConfiguration.fill" />
                        </div>

                        <div class="section-item">
                            <span class="item-title">Border Radius</span>
                            <border-radius-select v-model="currentConfiguration.borderRadius" />
                        </div>
                    </div>
                </div>
                <div class="divider" />
                <div class="preview-section">
                    <h4 class="section-title">Idle State Preview</h4>
                    <div class="preview-container">
                        <div class="demo-button" :style="demoButtonStyles">Button</div>
                    </div>
                    <h4 class="section-title">Hover State Preview</h4>
                    <div class="preview-container">
                        <div class="demo-button" :style="demoButtonHoverStyles">Button</div>
                    </div>
                    <h4 class="section-title">Clicked State Preview</h4>
                    <div class="preview-container">
                        <div class="demo-button" :style="demoButtonClickedStyles">Button</div>
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="close-button" @click="closeWidget"> Close </app-button>
                </div>
                <div class="action-container">
                    <app-button @click="handleSave"> Save & Close </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button.vue');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');

const BrandingColorSetting = () =>
    import('@/components/account/customizations/magic-branding/branding-color-setting');
const AppFontSelect = () => import('@/components/common/app-font-select');
const ButtonFillSelect = () => import('@/components/common/button-fill-select');
const BorderRadiusSelect = () => import('@/components/common/border-radius-select');

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'magic-branding-button-widget',

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'branding-color-setting': BrandingColorSetting,
        'app-font-select': AppFontSelect,
        'button-fill-select': ButtonFillSelect,
        'border-radius-select': BorderRadiusSelect,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            currentConfiguration: {},

            defaultColors: ['#000000', '#9F9F9F', '#FFFFFF'],

            fontSizes: [
                '8',
                '9',
                '10',
                '11',
                '12',
                '13',
                '14',
                '15',
                '16',
                '17',
                '18',
                '19',
                '20',
                '22',
                '24',
                '26',
                '28',
                '30',
                '32',
                '34',
                '36',
                '40',
                '44',
                '48',
                '52',
                '56',
                '64',
                '72',
            ],
        };
    },

    computed: {
        brandBodyFont() {
            const { options } = this.widget;
            return options.bodyFont;
        },

        mainBrandColors() {
            const { options } = this.widget;
            const { brandColors } = options;
            const extraColors = brandColors.customColors.map(({ value }) => value);
            return [
                brandColors.primaryColor.value,
                brandColors.secondaryColor.value,
                brandColors.accentColor.value,
                ...extraColors,
            ];
        },

        id() {
            const { options } = this.widget;
            return options ? options.id : null;
        },

        type() {
            const { options } = this.widget;
            return options ? options.type : null;
        },

        title() {
            const { options } = this.widget;
            return options ? options.title : 'Button settings';
        },

        initialConfiguration() {
            const { options } = this.widget;
            const brandDefaultConfiguration = {
                textColor: {
                    branding: '',
                    value: '#FFFFFF',
                },

                buttonColor: {
                    branding: 'accentColor',
                    value: this.mainBrandColors[2],
                },

                fill: 'solid',
                fontSize: 14,
                fontFamily: this.brandBodyFont,

                borderRadius: 'large',
            };
            return options.configuration || brandDefaultConfiguration;
        },

        demoButtonStyles() {
            return this.$helpers.mapButtonConfigurationToStyles(this.currentConfiguration);
        },

        hoverClickedConfiguration() {
            const textColor =
                this.currentConfiguration.fill === 'solid'
                    ? this.currentConfiguration.textColor.value
                    : '#FFFFFF';
            return { ...this.currentConfiguration, fill: 'solid', textColor: { value: textColor } };
        },

        demoButtonHoverStyles() {
            return {
                ...this.$helpers.mapButtonConfigurationToStyles(this.hoverClickedConfiguration),
                'background-image':
                    this.currentConfiguration.fill === 'solid'
                        ? 'linear-gradient(rgb(0 0 0/20%) 0 0)'
                        : 'none',
            };
        },

        demoButtonClickedStyles() {
            const percentageGradient = this.currentConfiguration.fill === 'solid' ? '45' : '20';
            return {
                ...this.$helpers.mapButtonConfigurationToStyles(this.hoverClickedConfiguration),
                'background-image': `linear-gradient(rgb(0 0 0/${percentageGradient}%) 0 0)`,
            };
        },
    },

    created() {
        this.currentConfiguration = { ...this.initialConfiguration };
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        handleSave() {
            var payload = {
                configuration: this.currentConfiguration,
                type: this.type,
                id: this.id,
            };
            EventBus.$emit('branding-button-payload-emitted', payload);
            this.closeWidget();
        },

        updateFontSize(value) {
            this.currentConfiguration.fontSize = parseInt(value);
        },

        updateFontFamily(value) {
            this.currentConfiguration.fontFamily = value;
        },

        handleColorChange(options, type) {
            const { color, key } = options;
            this.currentConfiguration[type] = {value: color, branding: key};
        },
    },
};
</script>

<style>
.magic-branding-button-widget.widget .content-wrapper {
    padding: 22px 35px 45px;
    display: flex;
    column-gap: 36px;
}

.magic-branding-button-widget .divider {
    border: 1px solid #cac6cb;
    width: 0px;
    height: 100%;
}

.magic-branding-button-widget .settings-section {
    width: 296px;
}

.magic-branding-button-widget .settings-section .settings-container {
    background: #e9e9ef 0% 0% no-repeat padding-box;
    box-shadow: inset -4px 3px 6px #8274e833;
    border: 1px solid #cac6cb;
    border-radius: 16px;
    padding: 16px;
}

.magic-branding-button-widget
    .settings-section
    .settings-container
    .section-item:not(.no-margin, :last-child) {
    margin-bottom: 8px;
}

.magic-branding-button-widget .settings-section .settings-container .section-item .item-title {
    font: normal normal normal 12px/11px Roboto;
}

.magic-branding-button-widget .settings-section .settings-container .app-font-select {
    column-gap: 8px;
}

.magic-branding-button-widget
    .settings-section
    .settings-container
    .app-multiselect-new.multiselect {
    min-height: 32px;
}

.magic-branding-button-widget
    .settings-section
    .settings-container
    .app-multiselect-new.multiselect
    .multiselect__tags {
    min-height: 32px;
    padding: 4px 27px 4px 8px;
    border: 1px solid #cac6cb;
}

.magic-branding-button-widget
    .settings-section
    .settings-container
    .app-multiselect-new.multiselect
    .multiselect__tags:hover {
    border: 2px solid #cac6cb;
}

.magic-branding-button-widget
    .settings-section
    .settings-container
    .app-multiselect-new.multiselect
    .multiselect__select {
    height: 30px;
}

.magic-branding-button-widget
    .settings-section
    .settings-container
    .app-multiselect-new.multiselect
    .multiselect__select::before {
    top: 75%;
}

.magic-branding-button-widget .settings-section .settings-container .multiselect__input {
    margin-bottom: 0;
    font-size: 14px;
}

.magic-branding-button-widget .settings-section .settings-container .border-radius-select {
    width: 100%;
    height: 40px;
}

.magic-branding-button-widget
    .settings-section
    .settings-container
    .border-radius-select
    .button-set {
    flex-grow: 1;
}

.magic-branding-button-widget
    .settings-section
    .settings-container
    .border-radius-select
    .button-set
    svg {
    padding: 6px;
    width: 100%;
}

.magic-branding-button-widget .preview-container {
    background: #e9e9ef 0% 0% no-repeat padding-box;
    box-shadow: inset 0px 3px 11px #8274e833;
    border: 1px solid #cac6cb;
    border-radius: 16px;
    padding: 48px;
    width: 192px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 14px;
    overflow: hidden;
}

.magic-branding-button-widget .preview-container .demo-button {
    padding: 16px 32px;
    cursor: default;
}

.magic-branding-button-widget .content-wrapper .section-title {
    font: normal normal 500 16px/24px Roboto;
}

.magic-branding-button-widget .app-button.normal.close-button {
    background: transparent;
    color: #382153;
    border: 1px solid #cac6cb;
    padding: 0 46px;
}

.magic-branding-button-widget .app-button.normal.close-button:hover {
    background: #e1e0e5;
    color: #382153;
}
</style>
